import moment from 'moment-timezone';
import {Record, List} from 'immutable';

import SessionBooking from 'web-app/react/entities/common/session-booking';

import {weekdayStrings, dayStrings} from './constants';

const Weekday = Record({
    day: '',
    editing: false,
    sessionBookings: List(),
    productBookings: List(),
});

export const allWeekdays = List(
    dayStrings.map(
        d =>
            new Weekday({
                day: d,
            }),
    ),
);

export const getOpeningHours = openingHours =>
    openingHours.reduce((aggr, openingHour) => {
        const dayString = dayStrings[openingHour.day - 1];
        const newObj = {};

        const openFrom = `${openingHour.fromHour}:${openingHour.fromMinute === 0 ? '00' : openingHour.fromMinute}`;
        const openTo = `${openingHour.toHour}:${openingHour.toMinute === 0 ? '00' : openingHour.toMinute}`;

        newObj[dayString] = {
            open: openingHour.open,
            day: openingHour.day,
            from: openFrom,
            to: openTo,
        };

        return Object.assign({}, aggr, newObj);
    }, {});

const momentFormat = 'HH:mm';

export const getMinMaxTimeFromIntervals = intervals => {
    return intervals.reduce(
        (obj, interval) => {
            return {
                min: obj.min.isAfter(interval.min) ? interval.min : obj.min,
                max: obj.max.isBefore(interval.max) ? interval.max : obj.max,
            };
        },
        {
            min: moment('23:59', momentFormat),
            max: moment('00:00', momentFormat),
        },
    );
};

const getOpeningHourIntervals = formattedOpeningHours => {
    return (
        Object.keys(formattedOpeningHours)
            // When finding the min/max time we don't take closed days into consideration
            .filter(ohk => formattedOpeningHours[ohk].open)
            .map(ohk => {
                const day = formattedOpeningHours[ohk];
                return {
                    day: day.day,
                    min: moment(day.from, momentFormat),
                    max: moment(day.to, momentFormat),
                };
            })
    );
};

export const getOpeningHourIntervalsFromApi = openingHours => {
    return getOpeningHourIntervals(getOpeningHours(openingHours));
};

export const sessionAndOpeningHoursToMinMaxTime = (availableSessions, planUsedSessions, openingHours, bookings) => {
    // The strategy is to convert session start/end and opening hours from/to to intervals (max/min)
    // and then reduce them to a single interval object indicating the global min/max times

    const sessionIntervals = availableSessions
        .concat(planUsedSessions)
        .flatMap(session => session.availability)
        .map(availability => ({min: availability.start, max: availability.end}))
        .toJS();

    const bookingIntervals = bookings
        ? bookings
              .filter(booking => booking.start)
              .map(booking => ({min: booking.start, max: booking.end}))
              .toJS()
        : [];

    const intervals = getOpeningHourIntervals(openingHours).concat(sessionIntervals).concat(bookingIntervals);

    return getMinMaxTimeFromIntervals(intervals);
};

export const getWeeklyMinTime = weeklyMinMaxTime => weeklyMinMaxTime.min.format(momentFormat);
export const getWeeklyMaxTime = weeklyMinMaxTime => weeklyMinMaxTime.max.format(momentFormat);
export const getFilteredWeekdays = (weekdays, showWeekends) =>
    showWeekends ? weekdays : weekdays.filter(wd => weekdayStrings.includes(wd.day));

const updateWeekdayFromPlanPart = (weekday, planPart) =>
    weekday.merge({
        sessionBookings: planPart.sessionBookings.filter(booking => weekday.day === booking.day),
        productBookings: planPart.productBookings.filter(booking => weekday.day === booking.day),
    });

/** *
 * Returns a new array of weekdays that are populated with the bookings of the planPart to be edited
 * @param existingWeekdays
 * @param planPart
 * @returns {Array}
 */
export const getBookingsUpdatedWeekdays = (existingWeekdays, planPart) => {
    return existingWeekdays.map(wd => updateWeekdayFromPlanPart(wd, planPart));
};

/** *
 * Returns a new array of weekdays that are populated with the session bookings updated with the new funded minutes
 * @param existingWeekdays
 * @param sessionBookings
 * @returns {Array}
 */
export const getUpdatedSessionBookings = (existingWeekdays, sessionBookings) => {
    return existingWeekdays.map(wd => {
        const dayBookings = List(
            sessionBookings.filter(sb => wd.day === sb.day).map(booking => SessionBooking.fromAPI(booking)),
        );
        return wd.merge({
            sessionBookings: dayBookings,
        });
    });
};

export const planPartToWeekday = (planPart, day) => {
    const newDay = new Weekday({
        day,
    });
    return updateWeekdayFromPlanPart(newDay, planPart);
};

/**
 * Formats a List of selected packages by extracting their `title` property
 * and concatenating the titles into a single string separated by commas.
 * @param {List<Object>} selectedPackages
 * @returns {string}
 */
export const formatSelectedPackages = selectedPackages => selectedPackages.map(pkg => pkg.title).join(', ');
